import { useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MessageCircle, Sparkles, PartyPopper } from "lucide-react";
import confetti from "canvas-confetti";

interface TutorialCompletionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function TutorialCompletionModal({ isOpen, onClose }: TutorialCompletionModalProps) {
  useEffect(() => {
    if (isOpen) {
      // Fire intense confetti
      const duration = 3000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 100 };

      function randomInRange(min: number, max: number) {
        return Math.random() * (max - min) + min;
      }

      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          return clearInterval(interval);
        }

        const particleCount = 50 * (timeLeft / duration);
        
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
          colors: ["#7c3aed", "#a855f7", "#c084fc", "#e879f9", "#22c55e", "#facc15"],
        });
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
          colors: ["#7c3aed", "#a855f7", "#c084fc", "#e879f9", "#22c55e", "#facc15"],
        });
      }, 250);

      return () => clearInterval(interval);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md text-center">
        <DialogHeader className="items-center">
          <div className="mb-4 flex justify-center">
            <div className="relative">
              <div className="absolute -inset-4 rounded-full bg-primary/20 animate-pulse" />
              <div className="relative p-4 rounded-full bg-gradient-to-br from-primary to-accent shadow-glow">
                <PartyPopper className="h-10 w-10 text-primary-foreground" />
              </div>
            </div>
          </div>
          <DialogTitle className="text-2xl flex items-center justify-center gap-2">
            <Sparkles className="h-6 w-6 text-primary" />
            ¡Felicitaciones!
            <Sparkles className="h-6 w-6 text-primary" />
          </DialogTitle>
          <DialogDescription className="text-base pt-2">
            Completaste el tutorial de DoctIA
          </DialogDescription>
        </DialogHeader>

        <div className="py-6 space-y-4">
          <p className="text-foreground">
            Ya conocés todas las herramientas para potenciar tu trabajo legal con inteligencia artificial.
          </p>
          
          <div className="p-4 rounded-lg bg-primary/5 border border-primary/10">
            <p className="text-sm text-muted-foreground mb-2">
              ¿Tenés dudas o sugerencias?
            </p>
            <a
              href="https://wa.me/5491155698892?text=Tengo%20una%20duda%20sobre%20DoctIA"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="outline" className="gap-2 w-full">
                <MessageCircle className="h-4 w-4" />
                Escribinos por WhatsApp
              </Button>
            </a>
          </div>
        </div>

        <Button onClick={onClose} className="w-full">
          ¡Empezar a usar DoctIA!
        </Button>
      </DialogContent>
    </Dialog>
  );
}
